// ============================================
// TEEMPAD EVENTS - IndexedDB (Dexie) + Firestore
// ============================================
//
// Offline-first: all writes go to IndexedDB first, then sync to Firestore.
// Pass { skipSync: true } to skip Firestore (used when pulling FROM Firestore).
// ============================================

import Dexie, { type Table } from 'dexie'
import { doc, getDoc, setDoc, getDocs, collection, collectionGroup, query, where } from '@/services/firestore'
import { getDocFromServer } from 'firebase/firestore'
import { waitForPendingWrites } from 'firebase/firestore'
import { db, isFirebaseConfigured } from './firebase'
import { devLog } from '@/utils/logger'
import type {
  User, Organization, OrgMember, OrgRole, Event, Venue, POI,
  KanbanColumn, EventTask, EventType, Subscription,
  ScheduleItem, Activity, EventEntity, EventTeam, TeamParticipant,
  TournamentConfig, TournamentGroup,
  Announcement, BudgetItem, EventDocument, Accreditation,
  SponsorTier, EventSponsor, SponsorDeliverable,
  SponsorDossier, DossierView,
  SponsorAccessToken, SponsorMedia,
  EnrollmentConfig, Enrollment,
  EntityDelegate, EventNotification, NotificationReceipt, UserFcmToken
} from '@/types'
import { EVENT_TASK_TEMPLATES } from '@/types'

function toPlainObject<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj))
}

interface SyncOptions {
  skipSync?: boolean
}

class TeempadEventsDatabase extends Dexie {
  users!: Table<User>
  organizations!: Table<Organization>
  orgMembers!: Table<OrgMember>
  events!: Table<Event>
  venues!: Table<Venue>
  pois!: Table<POI>
  kanbanColumns!: Table<KanbanColumn>
  eventTasks!: Table<EventTask>
  scheduleItems!: Table<ScheduleItem>
  activities!: Table<Activity>
  eventEntities!: Table<EventEntity>
  eventTeams!: Table<EventTeam>
  teamParticipants!: Table<TeamParticipant>
  tournamentConfigs!: Table<TournamentConfig>
  tournamentGroups!: Table<TournamentGroup>
  announcements!: Table<Announcement>
  budgetItems!: Table<BudgetItem>
  eventDocuments!: Table<EventDocument>
  accreditations!: Table<Accreditation>
  sponsorTiers!: Table<SponsorTier>
  eventSponsors!: Table<EventSponsor>
  sponsorDeliverables!: Table<SponsorDeliverable>
  sponsorDossiers!: Table<SponsorDossier>
  dossierViews!: Table<DossierView>
  sponsorAccessTokens!: Table<SponsorAccessToken>
  sponsorMedia!: Table<SponsorMedia>
  enrollmentConfigs!: Table<EnrollmentConfig>
  enrollments!: Table<Enrollment>
  entityDelegates!: Table<EntityDelegate>
  eventNotifications!: Table<EventNotification>
  notificationReceipts!: Table<NotificationReceipt>
  userFcmTokens!: Table<UserFcmToken>

  constructor() {
    super('TeempadEvents')
    this.version(3).stores({
      users: 'id, email',
      organizations: 'id, ownerId, name',
      orgMembers: 'id, orgId, userId',
      events: 'id, orgId, slug, status, startDate, createdAt',
      venues: 'id, eventId, orgId',
      pois: 'id, eventId, orgId'
    })
    this.version(4).stores({
      users: 'id, email',
      organizations: 'id, ownerId, name',
      orgMembers: 'id, orgId, userId',
      events: 'id, orgId, slug, status, startDate, createdAt',
      venues: 'id, eventId, orgId',
      pois: 'id, eventId, orgId',
      kanbanColumns: 'id, eventId, orgId, order',
      eventTasks: 'id, eventId, orgId, columnId, dueDate, *assigneeIds'
    })
    // v5: All new feature tables
    this.version(5).stores({
      users: 'id, email',
      organizations: 'id, ownerId, name',
      orgMembers: 'id, orgId, userId',
      events: 'id, orgId, slug, status, startDate, createdAt',
      venues: 'id, eventId, orgId',
      pois: 'id, eventId, orgId',
      kanbanColumns: 'id, eventId, orgId, order',
      eventTasks: 'id, eventId, orgId, columnId, dueDate, *assigneeIds',
      scheduleItems: 'id, eventId, orgId, date, type, status, groupId',
      activities: 'id, eventId, orgId, date',
      eventTeams: 'id, eventId, orgId, status',
      teamParticipants: 'id, eventId, orgId, teamId',
      tournamentConfigs: 'id, eventId, orgId',
      tournamentGroups: 'id, eventId, orgId, order',
      announcements: 'id, eventId, orgId, createdAt',
      budgetItems: 'id, eventId, orgId, type, category',
      eventDocuments: 'id, eventId, orgId',
      accreditations: 'id, eventId, orgId, code, teamId, type'
    })
    // v6: Sponsors module
    this.version(6).stores({
      users: 'id, email',
      organizations: 'id, ownerId, name',
      orgMembers: 'id, orgId, userId',
      events: 'id, orgId, slug, status, startDate, createdAt',
      venues: 'id, eventId, orgId',
      pois: 'id, eventId, orgId',
      kanbanColumns: 'id, eventId, orgId, order',
      eventTasks: 'id, eventId, orgId, columnId, dueDate, *assigneeIds',
      scheduleItems: 'id, eventId, orgId, date, type, status, groupId',
      activities: 'id, eventId, orgId, date',
      eventTeams: 'id, eventId, orgId, status',
      teamParticipants: 'id, eventId, orgId, teamId',
      tournamentConfigs: 'id, eventId, orgId',
      tournamentGroups: 'id, eventId, orgId, order',
      announcements: 'id, eventId, orgId, createdAt',
      budgetItems: 'id, eventId, orgId, type, category',
      eventDocuments: 'id, eventId, orgId',
      accreditations: 'id, eventId, orgId, code, teamId, type',
      sponsorTiers: 'id, eventId, orgId, order',
      eventSponsors: 'id, eventId, orgId, tierId, status',
      sponsorDeliverables: 'id, eventId, orgId, sponsorId'
    })
    // v7: Dossier generator + Sponsor portal
    this.version(7).stores({
      users: 'id, email',
      organizations: 'id, ownerId, name',
      orgMembers: 'id, orgId, userId',
      events: 'id, orgId, slug, status, startDate, createdAt',
      venues: 'id, eventId, orgId',
      pois: 'id, eventId, orgId',
      kanbanColumns: 'id, eventId, orgId, order',
      eventTasks: 'id, eventId, orgId, columnId, dueDate, *assigneeIds',
      scheduleItems: 'id, eventId, orgId, date, type, status, groupId',
      activities: 'id, eventId, orgId, date',
      eventTeams: 'id, eventId, orgId, status',
      teamParticipants: 'id, eventId, orgId, teamId',
      tournamentConfigs: 'id, eventId, orgId',
      tournamentGroups: 'id, eventId, orgId, order',
      announcements: 'id, eventId, orgId, createdAt',
      budgetItems: 'id, eventId, orgId, type, category',
      eventDocuments: 'id, eventId, orgId',
      accreditations: 'id, eventId, orgId, code, teamId, type',
      sponsorTiers: 'id, eventId, orgId, order',
      eventSponsors: 'id, eventId, orgId, tierId, status',
      sponsorDeliverables: 'id, eventId, orgId, sponsorId',
      sponsorDossiers: 'id, eventId, orgId, token',
      dossierViews: 'id, dossierId, eventId, orgId',
      sponsorAccessTokens: 'id, eventId, orgId, sponsorId, token',
      sponsorMedia: 'id, eventId, orgId, sponsorId, type'
    })
    // v8: Entity > Team hierarchy refactor
    this.version(8).stores({
      users: 'id, email',
      organizations: 'id, ownerId, name',
      orgMembers: 'id, orgId, userId',
      events: 'id, orgId, slug, status, startDate, createdAt',
      venues: 'id, eventId, orgId',
      pois: 'id, eventId, orgId',
      kanbanColumns: 'id, eventId, orgId, order',
      eventTasks: 'id, eventId, orgId, columnId, dueDate, *assigneeIds',
      scheduleItems: 'id, eventId, orgId, date, type, status, groupId',
      activities: 'id, eventId, orgId, date',
      eventEntities: 'id, eventId, orgId, status',
      eventTeams: 'id, eventId, orgId, entityId',
      teamParticipants: 'id, eventId, orgId, teamId',
      tournamentConfigs: 'id, eventId, orgId',
      tournamentGroups: 'id, eventId, orgId, order',
      announcements: 'id, eventId, orgId, createdAt',
      budgetItems: 'id, eventId, orgId, type, category',
      eventDocuments: 'id, eventId, orgId',
      accreditations: 'id, eventId, orgId, code, entityId, type',
      sponsorTiers: 'id, eventId, orgId, order',
      eventSponsors: 'id, eventId, orgId, tierId, status',
      sponsorDeliverables: 'id, eventId, orgId, sponsorId',
      sponsorDossiers: 'id, eventId, orgId, token',
      dossierViews: 'id, dossierId, eventId, orgId',
      sponsorAccessTokens: 'id, eventId, orgId, sponsorId, token',
      sponsorMedia: 'id, eventId, orgId, sponsorId, type'
    })
    // v9: Enrollment system
    this.version(9).stores({
      users: 'id, email',
      organizations: 'id, ownerId, name',
      orgMembers: 'id, orgId, userId',
      events: 'id, orgId, slug, status, startDate, createdAt',
      venues: 'id, eventId, orgId',
      pois: 'id, eventId, orgId',
      kanbanColumns: 'id, eventId, orgId, order',
      eventTasks: 'id, eventId, orgId, columnId, dueDate, *assigneeIds',
      scheduleItems: 'id, eventId, orgId, date, type, status, groupId',
      activities: 'id, eventId, orgId, date',
      eventEntities: 'id, eventId, orgId, status',
      eventTeams: 'id, eventId, orgId, entityId',
      teamParticipants: 'id, eventId, orgId, teamId',
      tournamentConfigs: 'id, eventId, orgId',
      tournamentGroups: 'id, eventId, orgId, order',
      announcements: 'id, eventId, orgId, createdAt',
      budgetItems: 'id, eventId, orgId, type, category',
      eventDocuments: 'id, eventId, orgId',
      accreditations: 'id, eventId, orgId, code, entityId, type',
      sponsorTiers: 'id, eventId, orgId, order',
      eventSponsors: 'id, eventId, orgId, tierId, status',
      sponsorDeliverables: 'id, eventId, orgId, sponsorId',
      sponsorDossiers: 'id, eventId, orgId, token',
      dossierViews: 'id, dossierId, eventId, orgId',
      sponsorAccessTokens: 'id, eventId, orgId, sponsorId, token',
      sponsorMedia: 'id, eventId, orgId, sponsorId, type',
      enrollmentConfigs: 'id, eventId, orgId, token',
      enrollments: 'id, eventId, orgId, configId, status'
    })
    // v10: Participant auth + notifications
    this.version(10).stores({
      users: 'id, email',
      organizations: 'id, ownerId, name',
      orgMembers: 'id, orgId, userId',
      events: 'id, orgId, slug, status, startDate, createdAt',
      venues: 'id, eventId, orgId',
      pois: 'id, eventId, orgId',
      kanbanColumns: 'id, eventId, orgId, order',
      eventTasks: 'id, eventId, orgId, columnId, dueDate, *assigneeIds',
      scheduleItems: 'id, eventId, orgId, date, type, status, groupId',
      activities: 'id, eventId, orgId, date',
      eventEntities: 'id, eventId, orgId, status',
      eventTeams: 'id, eventId, orgId, entityId',
      teamParticipants: 'id, eventId, orgId, teamId',
      tournamentConfigs: 'id, eventId, orgId',
      tournamentGroups: 'id, eventId, orgId, order',
      announcements: 'id, eventId, orgId, createdAt',
      budgetItems: 'id, eventId, orgId, type, category',
      eventDocuments: 'id, eventId, orgId',
      accreditations: 'id, eventId, orgId, code, entityId, type',
      sponsorTiers: 'id, eventId, orgId, order',
      eventSponsors: 'id, eventId, orgId, tierId, status',
      sponsorDeliverables: 'id, eventId, orgId, sponsorId',
      sponsorDossiers: 'id, eventId, orgId, token',
      dossierViews: 'id, dossierId, eventId, orgId',
      sponsorAccessTokens: 'id, eventId, orgId, sponsorId, token',
      sponsorMedia: 'id, eventId, orgId, sponsorId, type',
      enrollmentConfigs: 'id, eventId, orgId, token',
      enrollments: 'id, eventId, orgId, configId, status',
      entityDelegates: 'id, eventId, orgId, entityId, userId',
      eventNotifications: 'id, eventId, orgId, createdAt',
      notificationReceipts: 'id, notificationId, eventId, userId, read',
      userFcmTokens: 'id, userId'
    })
    this.version(11).stores({
      enrollmentConfigs: 'id, eventId, orgId, token, slug'
    })
  }
}

export const localDb = new TeempadEventsDatabase()

// --- User ---
export async function saveUser(user: User, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(user)
  await localDb.users.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'users', plain.id), plain)
  }
}

export async function getUser(userId: string): Promise<User | undefined> {
  return localDb.users.get(userId)
}

export async function getUserOrgs(userId: string): Promise<Organization[]> {
  const memberships = await localDb.orgMembers.where('userId').equals(userId).toArray()
  const orgIds = memberships.map(m => m.orgId)
  const orgs: Organization[] = []
  for (const id of orgIds) {
    const org = await localDb.organizations.get(id)
    if (org) orgs.push(org)
  }
  return orgs
}

// --- Organization ---
export async function saveOrganization(org: Organization, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(org)
  await localDb.organizations.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.id), plain)
  }
}

export async function getOrganization(orgId: string): Promise<Organization | undefined> {
  return localDb.organizations.get(orgId)
}

export async function getOrgMember(orgId: string, userId: string): Promise<OrgMember | undefined> {
  const members = await localDb.orgMembers.where('orgId').equals(orgId).filter(m => m.userId === userId).toArray()
  return members[0]
}

export async function getOrgMembers(orgId: string): Promise<OrgMember[]> {
  return localDb.orgMembers.where('orgId').equals(orgId).toArray()
}

export async function getOrgMembersWithUsers(orgId: string): Promise<(OrgMember & { user?: User })[]> {
  const members = await getOrgMembers(orgId)
  const result: (OrgMember & { user?: User })[] = []
  for (const m of members) {
    const user = await getUser(m.userId)
    result.push({ ...m, user })
  }
  return result
}

export async function addOrgMember(member: OrgMember): Promise<void> {
  const plain = toPlainObject(member)
  await localDb.orgMembers.put(plain)
  if (isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'members', plain.userId), plain)
  }
}

export async function getEvent(eventId: string): Promise<Event | undefined> {
  return localDb.events.get(eventId)
}

// --- Venues ---
export async function getEventVenues(eventId: string): Promise<Venue[]> {
  return localDb.venues.where('eventId').equals(eventId).toArray()
}

export async function saveVenue(venue: Venue, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(venue)
  await localDb.venues.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'venues', plain.id), plain)
  }
}

export async function deleteVenue(venue: Venue, opts?: SyncOptions): Promise<void> {
  await localDb.venues.delete(venue.id)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    const { deleteDoc, doc: docRef } = await import('firebase/firestore')
    await deleteDoc(docRef(db, 'organizations', venue.orgId, 'events', venue.eventId, 'venues', venue.id))
  }
}

// --- POIs ---
export async function getEventPOIs(eventId: string): Promise<POI[]> {
  return localDb.pois.where('eventId').equals(eventId).toArray()
}

export async function savePOI(poi: POI, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(poi)
  await localDb.pois.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'pois', plain.id), plain)
  }
}

export async function deletePOI(poi: POI, opts?: SyncOptions): Promise<void> {
  await localDb.pois.delete(poi.id)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    const { deleteDoc, doc: docRef } = await import('firebase/firestore')
    await deleteDoc(docRef(db, 'organizations', poi.orgId, 'events', poi.eventId, 'pois', poi.id))
  }
}

// --- Kanban Columns ---
export async function getEventKanbanColumns(eventId: string): Promise<KanbanColumn[]> {
  const cols = await localDb.kanbanColumns.where('eventId').equals(eventId).toArray()
  return cols.sort((a, b) => a.order - b.order)
}

export async function saveKanbanColumn(col: KanbanColumn, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(col)
  await localDb.kanbanColumns.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'kanbanColumns', plain.id), plain)
  }
}

export async function deleteKanbanColumn(col: KanbanColumn, opts?: SyncOptions): Promise<void> {
  await localDb.kanbanColumns.delete(col.id)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    const { deleteDoc, doc: docRef } = await import('firebase/firestore')
    await deleteDoc(docRef(db, 'organizations', col.orgId, 'events', col.eventId, 'kanbanColumns', col.id))
  }
}

/** Creates 3 default Kanban columns for a new event */
export async function initDefaultColumns(eventId: string, orgId: string): Promise<void> {
  const defaults = [
    { name: 'Pendiente', order: 0 },
    { name: 'En progreso', order: 1 },
    { name: 'Hecho', order: 2 }
  ]
  for (const d of defaults) {
    const col: KanbanColumn = {
      id: crypto.randomUUID().slice(0, 8),
      eventId,
      orgId,
      name: d.name,
      order: d.order
    }
    await saveKanbanColumn(col)
  }
}

/** Creates tasks from a template set for a given event type */
export async function applyTaskTemplate(eventId: string, orgId: string, eventType: EventType): Promise<void> {
  const templates = EVENT_TASK_TEMPLATES[eventType]
  if (!templates?.length) return
  const columns = await getEventKanbanColumns(eventId)
  const firstColId = columns[0]?.id ?? ''
  if (!firstColId) return
  const userId = localStorage.getItem('currentUserId') ?? ''
  const now = Date.now()
  for (let i = 0; i < templates.length; i++) {
    const tpl = templates[i]!
    const subtasks = tpl.subtasks?.map(s => ({
      id: crypto.randomUUID().slice(0, 6),
      title: s.title,
      done: s.done
    }))
    const task: EventTask = {
      id: crypto.randomUUID().slice(0, 8),
      eventId,
      orgId,
      columnId: firstColId,
      title: tpl.title,
      subtasks: subtasks?.length ? subtasks : undefined,
      assigneeIds: [],
      priority: tpl.priority,
      order: i,
      createdBy: userId,
      createdAt: now,
      updatedAt: now
    }
    await saveEventTask(task)
  }
}

// --- Event Tasks ---
export async function getEventTasks(eventId: string): Promise<EventTask[]> {
  return localDb.eventTasks.where('eventId').equals(eventId).toArray()
}

export async function saveEventTask(task: EventTask, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(task)
  await localDb.eventTasks.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'tasks', plain.id), plain)
  }
}

export async function deleteEventTask(task: EventTask, opts?: SyncOptions): Promise<void> {
  await localDb.eventTasks.delete(task.id)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    const { deleteDoc, doc: docRef } = await import('firebase/firestore')
    await deleteDoc(docRef(db, 'organizations', task.orgId, 'events', task.eventId, 'tasks', task.id))
  }
}

// --- Schedule Items ---
export async function getEventSchedule(eventId: string): Promise<ScheduleItem[]> {
  const items = await localDb.scheduleItems.where('eventId').equals(eventId).toArray()
  return items.sort((a, b) => a.date === b.date ? a.startTime.localeCompare(b.startTime) : a.date.localeCompare(b.date))
}

export async function saveScheduleItem(item: ScheduleItem, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(item)
  await localDb.scheduleItems.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'scheduleItems', plain.id), plain)
  }
}

export async function deleteScheduleItem(item: ScheduleItem, opts?: SyncOptions): Promise<void> {
  await localDb.scheduleItems.delete(item.id)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    const { deleteDoc, doc: docRef } = await import('firebase/firestore')
    await deleteDoc(docRef(db, 'organizations', item.orgId, 'events', item.eventId, 'scheduleItems', item.id))
  }
}

// --- Activities ---
export async function getEventActivities(eventId: string): Promise<Activity[]> {
  const items = await localDb.activities.where('eventId').equals(eventId).toArray()
  return items.sort((a, b) => a.date === b.date ? a.startTime.localeCompare(b.startTime) : a.date.localeCompare(b.date))
}

export async function saveActivity(activity: Activity, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(activity)
  await localDb.activities.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'activities', plain.id), plain)
  }
}

export async function deleteActivity(activity: Activity, opts?: SyncOptions): Promise<void> {
  await localDb.activities.delete(activity.id)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    const { deleteDoc, doc: docRef } = await import('firebase/firestore')
    await deleteDoc(docRef(db, 'organizations', activity.orgId, 'events', activity.eventId, 'activities', activity.id))
  }
}

// --- Event Entities (clubs, schools, associations) ---
export async function getEventEntities(eventId: string): Promise<EventEntity[]> {
  return localDb.eventEntities.where('eventId').equals(eventId).toArray()
}

export async function saveEventEntity(entity: EventEntity, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(entity)
  await localDb.eventEntities.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'entities', plain.id), plain)
  }
}

export async function deleteEventEntity(entity: EventEntity, opts?: SyncOptions): Promise<void> {
  const teams = await localDb.eventTeams.where('entityId').equals(entity.id).toArray()
  for (const t of teams) { await deleteEventTeam(t, opts) }
  await localDb.eventEntities.delete(entity.id)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    const { deleteDoc, doc: docRef } = await import('firebase/firestore')
    await deleteDoc(docRef(db, 'organizations', entity.orgId, 'events', entity.eventId, 'entities', entity.id))
  }
}

// --- Event Teams (competition units within entities) ---
export async function getEventTeams(eventId: string): Promise<EventTeam[]> {
  return localDb.eventTeams.where('eventId').equals(eventId).toArray()
}

export async function getEntityTeams(entityId: string): Promise<EventTeam[]> {
  return localDb.eventTeams.where('entityId').equals(entityId).toArray()
}

export async function saveEventTeam(team: EventTeam, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(team)
  await localDb.eventTeams.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'teams', plain.id), plain)
  }
}

export async function deleteEventTeam(team: EventTeam, opts?: SyncOptions): Promise<void> {
  const participants = await localDb.teamParticipants.where('teamId').equals(team.id).toArray()
  for (const p of participants) { await deleteTeamParticipant(p, opts) }
  await localDb.eventTeams.delete(team.id)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    const { deleteDoc, doc: docRef } = await import('firebase/firestore')
    await deleteDoc(docRef(db, 'organizations', team.orgId, 'events', team.eventId, 'teams', team.id))
  }
}

// --- Team Participants ---
export async function getTeamParticipants(teamId: string): Promise<TeamParticipant[]> {
  return localDb.teamParticipants.where('teamId').equals(teamId).toArray()
}

export async function getEventParticipants(eventId: string): Promise<TeamParticipant[]> {
  return localDb.teamParticipants.where('eventId').equals(eventId).toArray()
}

export async function saveTeamParticipant(p: TeamParticipant, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(p)
  await localDb.teamParticipants.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'teamParticipants', plain.id), plain)
  }
}

export async function deleteTeamParticipant(p: TeamParticipant, opts?: SyncOptions): Promise<void> {
  await localDb.teamParticipants.delete(p.id)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    const { deleteDoc, doc: docRef } = await import('firebase/firestore')
    await deleteDoc(docRef(db, 'organizations', p.orgId, 'events', p.eventId, 'teamParticipants', p.id))
  }
}

// --- Tournament Config ---
export async function getTournamentConfig(eventId: string): Promise<TournamentConfig | undefined> {
  return localDb.tournamentConfigs.where('eventId').equals(eventId).first()
}

export async function saveTournamentConfig(cfg: TournamentConfig, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(cfg)
  await localDb.tournamentConfigs.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'tournamentConfig', plain.id), plain)
  }
}

// --- Tournament Groups ---
export async function getTournamentGroups(eventId: string): Promise<TournamentGroup[]> {
  const groups = await localDb.tournamentGroups.where('eventId').equals(eventId).toArray()
  return groups.sort((a, b) => a.order - b.order)
}

export async function saveTournamentGroup(group: TournamentGroup, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(group)
  await localDb.tournamentGroups.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'tournamentGroups', plain.id), plain)
  }
}

export async function deleteTournamentGroup(group: TournamentGroup, opts?: SyncOptions): Promise<void> {
  await localDb.tournamentGroups.delete(group.id)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    const { deleteDoc, doc: docRef } = await import('firebase/firestore')
    await deleteDoc(docRef(db, 'organizations', group.orgId, 'events', group.eventId, 'tournamentGroups', group.id))
  }
}

// --- Announcements ---
export async function getEventAnnouncements(eventId: string): Promise<Announcement[]> {
  const items = await localDb.announcements.where('eventId').equals(eventId).toArray()
  return items.sort((a, b) => b.createdAt - a.createdAt)
}

export async function saveAnnouncement(a: Announcement, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(a)
  await localDb.announcements.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'announcements', plain.id), plain)
  }
}

export async function deleteAnnouncement(a: Announcement, opts?: SyncOptions): Promise<void> {
  await localDb.announcements.delete(a.id)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    const { deleteDoc, doc: docRef } = await import('firebase/firestore')
    await deleteDoc(docRef(db, 'organizations', a.orgId, 'events', a.eventId, 'announcements', a.id))
  }
}

// --- Budget ---
export async function getEventBudget(eventId: string): Promise<BudgetItem[]> {
  return localDb.budgetItems.where('eventId').equals(eventId).toArray()
}

export async function saveBudgetItem(item: BudgetItem, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(item)
  await localDb.budgetItems.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'budgetItems', plain.id), plain)
  }
}

export async function deleteBudgetItem(item: BudgetItem, opts?: SyncOptions): Promise<void> {
  await localDb.budgetItems.delete(item.id)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    const { deleteDoc, doc: docRef } = await import('firebase/firestore')
    await deleteDoc(docRef(db, 'organizations', item.orgId, 'events', item.eventId, 'budgetItems', item.id))
  }
}

// --- Event Documents ---
export async function getEventDocuments(eventId: string): Promise<EventDocument[]> {
  return localDb.eventDocuments.where('eventId').equals(eventId).toArray()
}

export async function saveEventDocument(d: EventDocument, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(d)
  await localDb.eventDocuments.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'documents', plain.id), plain)
  }
}

export async function deleteEventDocument(d: EventDocument, opts?: SyncOptions): Promise<void> {
  await localDb.eventDocuments.delete(d.id)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    const { deleteDoc, doc: docRef } = await import('firebase/firestore')
    await deleteDoc(docRef(db, 'organizations', d.orgId, 'events', d.eventId, 'documents', d.id))
  }
}

// --- Accreditations ---
export async function getEventAccreditations(eventId: string): Promise<Accreditation[]> {
  return localDb.accreditations.where('eventId').equals(eventId).toArray()
}

export async function saveAccreditation(a: Accreditation, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(a)
  await localDb.accreditations.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'accreditations', plain.id), plain)
  }
}

export async function deleteAccreditation(a: Accreditation, opts?: SyncOptions): Promise<void> {
  await localDb.accreditations.delete(a.id)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    const { deleteDoc, doc: docRef } = await import('firebase/firestore')
    await deleteDoc(docRef(db, 'organizations', a.orgId, 'events', a.eventId, 'accreditations', a.id))
  }
}

export async function findAccreditationByCode(eventId: string, code: string): Promise<Accreditation | undefined> {
  return localDb.accreditations.where({ eventId, code }).first()
}

// --- Events ---
export async function getOrgEvents(orgId: string): Promise<Event[]> {
  const list = await localDb.events.where('orgId').equals(orgId).toArray()
  return list.sort((a, b) => b.createdAt - a.createdAt)
}

export async function saveEvent(event: Event, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(event)
  await localDb.events.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.id), plain)
    await syncPublicEvent(plain)
  }
}

/** Publica un evento (actualiza status, guarda y sincroniza) */
export async function publishEvent(event: Event): Promise<void> {
  const updated = { ...event, status: 'published' as const, updatedAt: Date.now() }
  await saveEvent(updated)
}

export async function deleteEvent(event: Event): Promise<void> {
  const eventVenues = await localDb.venues.where('eventId').equals(event.id).toArray()
  const eventPois = await localDb.pois.where('eventId').equals(event.id).toArray()
  const eventCols = await localDb.kanbanColumns.where('eventId').equals(event.id).toArray()
  const eventTasksList = await localDb.eventTasks.where('eventId').equals(event.id).toArray()
  for (const c of eventCols) {
    try { await deleteKanbanColumn(c) } catch (err) { console.error('deleteKanbanColumn failed:', c.id, err) }
  }
  for (const t of eventTasksList) {
    try { await deleteEventTask(t) } catch (err) { console.error('deleteEventTask failed:', t.id, err) }
  }
  for (const v of eventVenues) {
    try {
      await deleteVenue(v)
    } catch (err) {
      console.error('deleteVenue failed:', v.id, err)
    }
  }
  for (const p of eventPois) {
    try {
      await deletePOI(p)
    } catch (err) {
      console.error('deletePOI failed:', p.id, err)
    }
  }
  await localDb.events.delete(event.id)
  if (isFirebaseConfigured() && db) {
    const { deleteDoc, doc: docRef } = await import('firebase/firestore')
    try {
      await deleteDoc(docRef(db, 'organizations', event.orgId, 'events', event.id))
      if (event.slug) {
        await deleteDoc(docRef(db, 'publicEvents', event.slug))
      }
    } catch (err) {
      console.error('deleteEvent Firestore failed:', err)
      throw err
    }
  }
}

/** Obtiene evento público por slug (sin auth) */
export async function getPublicEventBySlug(slug: string): Promise<PublicEvent | null> {
  if (!isFirebaseConfigured() || !db) return null
  try {
    const snap = await getDoc(doc(db, 'publicEvents', slug))
    if (snap.exists()) return snap.data() as PublicEvent
    return null
  } catch {
    return null
  }
}

/** Obtiene venues de un evento desde Firestore (para vista pública) */
export async function getEventVenuesFromFirestore(orgId: string, eventId: string): Promise<Venue[]> {
  if (!isFirebaseConfigured() || !db) return []
  try {
    const { getDocs, collection } = await import('firebase/firestore')
    const snap = await getDocs(collection(db, 'organizations', orgId, 'events', eventId, 'venues'))
    return snap.docs.map((d) => ({ ...(d.data() as Venue), id: d.id }))
  } catch {
    return []
  }
}

/** Obtiene POIs de un evento desde Firestore (para vista pública) */
export async function getEventPOIsFromFirestore(orgId: string, eventId: string): Promise<POI[]> {
  if (!isFirebaseConfigured() || !db) return []
  try {
    const { getDocs, collection } = await import('firebase/firestore')
    const snap = await getDocs(collection(db, 'organizations', orgId, 'events', eventId, 'pois'))
    return snap.docs.map((d) => ({ ...(d.data() as POI), id: d.id }))
  } catch {
    return []
  }
}

/** Obtiene sponsor tiers de un evento desde Firestore (para vista pública) */
export async function getEventSponsorTiersFromFirestore(orgId: string, eventId: string): Promise<SponsorTier[]> {
  if (!isFirebaseConfigured() || !db) return []
  try {
    const { getDocs, collection, query, orderBy } = await import('firebase/firestore')
    const snap = await getDocs(query(collection(db, 'organizations', orgId, 'events', eventId, 'sponsorTiers'), orderBy('order')))
    return snap.docs.map((d) => ({ ...(d.data() as SponsorTier), id: d.id }))
  } catch {
    return []
  }
}

/** Obtiene sponsors de un evento desde Firestore (para vista pública) */
/** Obtiene schedule items de un evento desde Firestore (para vista pública/participante) */
export async function getEventScheduleFromFirestore(orgId: string, eventId: string): Promise<ScheduleItem[]> {
  if (!isFirebaseConfigured() || !db) return []
  try {
    const { getDocs, collection } = await import('firebase/firestore')
    const snap = await getDocs(collection(db, 'organizations', orgId, 'events', eventId, 'scheduleItems'))
    return snap.docs.map(d => ({ ...(d.data() as ScheduleItem), id: d.id }))
      .sort((a, b) => a.date === b.date ? a.startTime.localeCompare(b.startTime) : a.date.localeCompare(b.date))
  } catch { return [] }
}

/** Obtiene actividades de un evento desde Firestore (para vista pública/participante) */
export async function getEventActivitiesFromFirestore(orgId: string, eventId: string): Promise<Activity[]> {
  if (!isFirebaseConfigured() || !db) return []
  try {
    const { getDocs, collection } = await import('firebase/firestore')
    const snap = await getDocs(collection(db, 'organizations', orgId, 'events', eventId, 'activities'))
    return snap.docs.map(d => ({ ...(d.data() as Activity), id: d.id }))
      .sort((a, b) => a.date === b.date ? a.startTime.localeCompare(b.startTime) : a.date.localeCompare(b.date))
  } catch { return [] }
}

/** Obtiene announcements de un evento desde Firestore (para participante) */
export async function getEventAnnouncementsFromFirestore(orgId: string, eventId: string): Promise<Announcement[]> {
  if (!isFirebaseConfigured() || !db) return []
  try {
    const { getDocs, collection } = await import('firebase/firestore')
    const snap = await getDocs(collection(db, 'organizations', orgId, 'events', eventId, 'announcements'))
    return snap.docs.map(d => ({ ...(d.data() as Announcement), id: d.id }))
      .sort((a, b) => b.createdAt - a.createdAt)
  } catch { return [] }
}

/** Obtiene entidades de un evento desde Firestore (para vista pública) */
export async function getEventEntitiesFromFirestore(orgId: string, eventId: string): Promise<EventEntity[]> {
  if (!isFirebaseConfigured() || !db) return []
  try {
    const { getDocs, collection } = await import('firebase/firestore')
    const snap = await getDocs(collection(db, 'organizations', orgId, 'events', eventId, 'entities'))
    return snap.docs.map((d) => ({ ...(d.data() as EventEntity), id: d.id }))
  } catch {
    return []
  }
}

/** Obtiene equipos de competición de un evento desde Firestore (para vista pública) */
export async function getEventTeamsFromFirestore(orgId: string, eventId: string): Promise<EventTeam[]> {
  if (!isFirebaseConfigured() || !db) return []
  try {
    const { getDocs, collection } = await import('firebase/firestore')
    const snap = await getDocs(collection(db, 'organizations', orgId, 'events', eventId, 'teams'))
    return snap.docs.map((d) => ({ ...(d.data() as EventTeam), id: d.id }))
  } catch {
    return []
  }
}

export async function getEventSponsorsFromFirestore(orgId: string, eventId: string): Promise<EventSponsor[]> {
  if (!isFirebaseConfigured() || !db) return []
  try {
    const { getDocs, collection } = await import('firebase/firestore')
    const snap = await getDocs(collection(db, 'organizations', orgId, 'events', eventId, 'sponsors'))
    return snap.docs.map((d) => ({ ...(d.data() as EventSponsor), id: d.id }))
  } catch {
    return []
  }
}

/** Evento público con datos de la organización para la vista sin auth */
export type PublicEvent = Event & {
  orgName?: string
  orgLogoUrl?: string
  enrollmentSlug?: string
  enrollmentDeadline?: string
}

/** Sincroniza evento publicado a publicEvents para enlaces públicos */
export async function syncPublicEvent(event: Event): Promise<void> {
  if (!isFirebaseConfigured() || !db) return
  try {
    if (event.status === 'published' && event.slug) {
      const org = await getOrganization(event.orgId)
      const enrollConfig = await getEnrollmentConfig(event.id)
      const publicDoc: PublicEvent = {
        ...event,
        orgName: org?.name,
        orgLogoUrl: org?.logoUrl,
        enrollmentSlug: enrollConfig?.status === 'open' ? (enrollConfig.slug || enrollConfig.token) : undefined,
        enrollmentDeadline: enrollConfig?.status === 'open' ? enrollConfig.deadline : undefined,
      }
      await setDoc(doc(db, 'publicEvents', event.slug), publicDoc)
    } else if (event.slug) {
      const { deleteDoc, doc: docRef } = await import('firebase/firestore')
      await deleteDoc(docRef(db, 'publicEvents', event.slug))
    }
  } catch (err) {
    console.error('syncPublicEvent failed:', err)
  }
}

/** Obtiene eventos públicos para la sección de descubrimiento */
export async function getDiscoverEvents(maxResults = 12): Promise<PublicEvent[]> {
  if (!isFirebaseConfigured() || !db) return []
  try {
    const { getDocs, collection, query, where, orderBy, limit } = await import('firebase/firestore')
    const today = new Date().toISOString().slice(0, 10)
    const snap = await getDocs(
      query(
        collection(db, 'publicEvents'),
        where('endDate', '>=', today),
        orderBy('endDate'),
        limit(maxResults)
      )
    )
    return snap.docs.map((d) => d.data() as PublicEvent)
  } catch (err) {
    console.error('getDiscoverEvents failed:', err)
    return []
  }
}

// --- Firestore sync (creates user from Auth if not in Firestore, e.g. Google SSO) ---
export async function syncUserFromFirestore(
  userId: string,
  firebaseUser?: { email: string | null; displayName: string | null; photoURL: string | null }
): Promise<void> {
  if (!isFirebaseConfigured() || !db) return
  try {
    const userRef = doc(db, 'users', userId)
    const snap = await getDoc(userRef)
    if (snap.exists()) {
      const data = snap.data() as User
      await saveUser({ ...data, id: snap.id }, { skipSync: true })
    } else if (firebaseUser) {
      const now = Date.now()
      const user: User = {
        id: userId,
        email: firebaseUser.email ?? '',
        displayName: firebaseUser.displayName ?? '',
        photoUrl: firebaseUser.photoURL ?? undefined,
        createdAt: now,
        lastLoginAt: now
      }
      await saveUser(user)
    }
  } catch (err) {
    console.error('syncUserFromFirestore failed:', err)
    if (firebaseUser) {
      const now = Date.now()
      const user: User = {
        id: userId,
        email: firebaseUser.email ?? '',
        displayName: firebaseUser.displayName ?? '',
        photoUrl: firebaseUser.photoURL ?? undefined,
        createdAt: now,
        lastLoginAt: now
      }
      await saveUser(user, { skipSync: true })
    }
    throw err
  }
}

/** Sube todas las sedes locales (IndexedDB) a Firestore. Devuelve el número sincronizado. */
export async function syncVenuesToFirestore(): Promise<{ synced: number; failed: number }> {
  if (!isFirebaseConfigured() || !db) return { synced: 0, failed: 0 }
  const venues = await localDb.venues.toArray()
  let synced = 0
  let failed = 0
  for (const venue of venues) {
    try {
      const venueRef = doc(db, 'organizations', venue.orgId, 'events', venue.eventId, 'venues', venue.id)
      await setDoc(venueRef, venue)
      synced++
    } catch (err) {
      console.error('syncVenue failed:', venue.id, err)
      failed++
    }
  }
  return { synced, failed }
}

/** Asegura que la organización y el miembro estén en Firestore (para reglas de invitación) */
async function ensureOrgAndMemberInFirestore(orgId: string, userId: string): Promise<void> {
  if (!isFirebaseConfigured() || !db) return
  const org = await getOrganization(orgId)
  if (org) {
    const orgRef = doc(db, 'organizations', orgId)
    const orgSnap = await getDoc(orgRef)
    if (!orgSnap.exists()) {
      await setDoc(orgRef, org)
    }
  }
  await syncOrgMembersToFirestore(userId)
}

/** Crea un enlace de invitación a la organización. Devuelve la URL. Requiere Firebase. */
export async function createOrgInvite(orgId: string, role: OrgRole, invitedBy: string): Promise<string> {
  if (!isFirebaseConfigured() || !db) {
    throw new Error('Firebase no está configurado. Las invitaciones requieren conexión a Firestore.')
  }
  await ensureOrgAndMemberInFirestore(orgId, invitedBy)
  const org = await getOrganization(orgId)
  const orgName = org?.name ?? 'Organización'
  const token = crypto.randomUUID().replace(/-/g, '').slice(0, 16)
  const invite = { orgId, orgName, role, invitedBy, createdAt: Date.now(), expiresAt: Date.now() + 7 * 24 * 60 * 60 * 1000 }
  try {
    await setDoc(doc(db, 'orgInvites', token), invite)
    await waitForPendingWrites(db)
    devLog.log('Invite created:', token, 'expires:', new Date(invite.expiresAt).toISOString())
  } catch (err) {
    console.error('createOrgInvite Firestore error:', err)
    throw new Error(
      'No se pudo crear la invitación. Comprueba que la organización y tu usuario estén sincronizados con Firestore.'
    )
  }
  const base = `${window.location.origin}${import.meta.env.BASE_URL}`
  return `${base}join/${token}`
}

/** Sincroniza datos de todas las orgs del usuario desde Firestore (para miembros invitados) */
export async function syncUserOrgsDataFromFirestore(userId: string): Promise<void> {
  const memberships = await localDb.orgMembers.where('userId').equals(userId).toArray()
  for (const m of memberships) {
    try {
      await syncOrgDataFromFirestore(m.orgId)
    } catch (err) {
      console.error('syncOrgDataFromFirestore failed for org', m.orgId, err)
    }
  }
}

/** Descarga org, eventos, venues y POIs desde Firestore al IndexedDB local (para miembros invitados) */
export async function syncOrgDataFromFirestore(orgId: string): Promise<void> {
  if (!isFirebaseConfigured() || !db) return
  try {
    const orgSnap = await getDoc(doc(db, 'organizations', orgId))
    if (orgSnap.exists()) {
      const org = { ...(orgSnap.data() as Organization), id: orgSnap.id }
      await saveOrganization(org, { skipSync: true })
    }
    // Sync org members + their User profiles (replace local list with Firestore truth)
    const membersSnap = await getDocs(collection(db, 'organizations', orgId, 'members'))
    await localDb.orgMembers.where('orgId').equals(orgId).delete()
    for (const mDoc of membersSnap.docs) {
      const member = { ...(mDoc.data() as OrgMember), id: mDoc.id }
      await localDb.orgMembers.put(toPlainObject(member))
      // Fetch user profile so getOrgMembersWithUsers can resolve names
      try {
        const userSnap = await getDoc(doc(db, 'users', member.userId))
        if (userSnap.exists()) {
          await saveUser({ ...(userSnap.data() as User), id: userSnap.id }, { skipSync: true })
        }
      } catch { /* user doc may not exist yet */ }
    }

    const eventsSnap = await getDocs(collection(db, 'organizations', orgId, 'events'))
    for (const eventDoc of eventsSnap.docs) {
      const event = { ...(eventDoc.data() as Event), id: eventDoc.id }
      await saveEvent(event, { skipSync: true })
      const venuesSnap = await getDocs(collection(db, 'organizations', orgId, 'events', eventDoc.id, 'venues'))
      for (const v of venuesSnap.docs) {
        const venue = { ...(v.data() as Venue), id: v.id }
        await saveVenue(venue, { skipSync: true })
      }
      const poisSnap = await getDocs(collection(db, 'organizations', orgId, 'events', eventDoc.id, 'pois'))
      for (const p of poisSnap.docs) {
        const poi = { ...(p.data() as POI), id: p.id }
        await savePOI(poi, { skipSync: true })
      }
      const colsSnap = await getDocs(collection(db, 'organizations', orgId, 'events', eventDoc.id, 'kanbanColumns'))
      for (const c of colsSnap.docs) {
        const col = { ...(c.data() as KanbanColumn), id: c.id }
        await saveKanbanColumn(col, { skipSync: true })
      }
      const tasksSnap = await getDocs(collection(db, 'organizations', orgId, 'events', eventDoc.id, 'tasks'))
      for (const t of tasksSnap.docs) {
        const task = { ...(t.data() as EventTask), id: t.id }
        await saveEventTask(task, { skipSync: true })
      }
      const newCollections: Array<{ name: string; save: (data: Record<string, unknown>) => Promise<void> }> = [
        { name: 'scheduleItems', save: async (d) => { await saveScheduleItem(d as unknown as ScheduleItem, { skipSync: true }) } },
        { name: 'activities', save: async (d) => { await saveActivity(d as unknown as Activity, { skipSync: true }) } },
        { name: 'entities', save: async (d) => { await saveEventEntity(d as unknown as EventEntity, { skipSync: true }) } },
        { name: 'teams', save: async (d) => { await saveEventTeam(d as unknown as EventTeam, { skipSync: true }) } },
        { name: 'teamParticipants', save: async (d) => { await saveTeamParticipant(d as unknown as TeamParticipant, { skipSync: true }) } },
        { name: 'tournamentConfig', save: async (d) => { await saveTournamentConfig(d as unknown as TournamentConfig, { skipSync: true }) } },
        { name: 'tournamentGroups', save: async (d) => { await saveTournamentGroup(d as unknown as TournamentGroup, { skipSync: true }) } },
        { name: 'announcements', save: async (d) => { await saveAnnouncement(d as unknown as Announcement, { skipSync: true }) } },
        { name: 'budgetItems', save: async (d) => { await saveBudgetItem(d as unknown as BudgetItem, { skipSync: true }) } },
        { name: 'documents', save: async (d) => { await saveEventDocument(d as unknown as EventDocument, { skipSync: true }) } },
        { name: 'accreditations', save: async (d) => { await saveAccreditation(d as unknown as Accreditation, { skipSync: true }) } },
        { name: 'sponsorTiers', save: async (d) => { await saveSponsorTier(d as unknown as SponsorTier, { skipSync: true }) } },
        { name: 'sponsors', save: async (d) => { await saveEventSponsor(d as unknown as EventSponsor, { skipSync: true }) } },
        { name: 'sponsorDeliverables', save: async (d) => { await saveSponsorDeliverable(d as unknown as SponsorDeliverable, { skipSync: true }) } },
        { name: 'sponsorDossiers', save: async (d) => { await saveSponsorDossier(d as unknown as SponsorDossier, { skipSync: true }) } },
        { name: 'dossierViews', save: async (d) => { await saveDossierView(d as unknown as DossierView, { skipSync: true }) } },
        { name: 'sponsorAccessTokens', save: async (d) => { await saveSponsorAccessToken(d as unknown as SponsorAccessToken, { skipSync: true }) } },
        { name: 'sponsorMedia', save: async (d) => { await saveSponsorMedia(d as unknown as SponsorMedia, { skipSync: true }) } },
        { name: 'enrollmentConfigs', save: async (d) => { await saveEnrollmentConfig(d as unknown as EnrollmentConfig, { skipSync: true }) } },
        { name: 'enrollments', save: async (d) => { await saveEnrollment(d as unknown as Enrollment, { skipSync: true }) } },
        { name: 'entityDelegates', save: async (d) => { await saveEntityDelegate(d as unknown as EntityDelegate, { skipSync: true }) } },
        { name: 'notifications', save: async (d) => { await saveEventNotification(d as unknown as EventNotification, { skipSync: true }) } },
        { name: 'notificationReceipts', save: async (d) => { await saveNotificationReceipt(d as unknown as NotificationReceipt, { skipSync: true }) } },
      ]
      for (const { name, save } of newCollections) {
        try {
          const snap = await getDocs(collection(db, 'organizations', orgId, 'events', eventDoc.id, name))
          for (const d of snap.docs) { await save({ ...d.data(), id: d.id }) }
        } catch { /* collection may not exist yet */ }
      }
      // Re-sync publicEvent with enrollment info now that all subcollections are in IndexedDB
      if (event.status === 'published' && event.slug) {
        await syncPublicEvent(event)
      }
    }
  } catch (err) {
    console.error('syncOrgDataFromFirestore failed:', err)
    throw err
  }
}

/** Acepta una invitación y añade al usuario a la organización. Sincroniza org y eventos desde Firestore. */
export async function acceptOrgInvite(token: string, userId: string): Promise<boolean> {
  if (!isFirebaseConfigured() || !db) return false
  try {
    const snap = await getDoc(doc(db, 'orgInvites', token))
    if (!snap.exists()) return false
    const data = snap.data() as { orgId: string; role: string; expiresAt: number }
    if (data.expiresAt < Date.now()) return false
    const member: OrgMember = {
      id: `${data.orgId}-${userId}`,
      orgId: data.orgId,
      userId,
      role: (data.role as OrgRole) || 'member',
      joinedAt: Date.now()
    }
    await addOrgMember(member)
    await syncOrgDataFromFirestore(data.orgId)
    const { deleteDoc, doc: docRef } = await import('firebase/firestore')
    await deleteDoc(docRef(db, 'orgInvites', token))
    return true
  } catch {
    return false
  }
}

export type OrgInviteResult =
  | { ok: true; orgId: string; orgName: string; role: string }
  | { ok: false; reason: 'not_found' | 'expired' | 'unavailable' }

/** Obtiene los datos de una invitación (para mostrar org name antes de aceptar) */
export async function getOrgInvite(token: string): Promise<OrgInviteResult> {
  if (!isFirebaseConfigured() || !db) {
    return { ok: false, reason: 'unavailable' }
  }
  try {
    const snap = await getDocFromServer(doc(db, 'orgInvites', token))
    if (!snap.exists()) {
      devLog.log('Invite not found:', token, '(check URL matches token from created invite)')
      return { ok: false, reason: 'not_found' }
    }
    const data = snap.data() as { orgId: string; orgName?: string; role: string; expiresAt: number }
    if (data.expiresAt < Date.now()) {
      return { ok: false, reason: 'expired' }
    }
    const orgName = data.orgName ?? 'Organización'
    return { ok: true, orgId: data.orgId, orgName, role: data.role }
  } catch (err) {
    console.error('getOrgInvite error:', err)
    return { ok: false, reason: 'unavailable' }
  }
}

/** Descarga membresías del usuario desde Firestore al IndexedDB local (collection group query) */
export async function syncOrgMembersFromFirestore(userId: string): Promise<void> {
  if (!isFirebaseConfigured() || !db) return
  try {
    const membersQuery = query(collectionGroup(db, 'members'), where('userId', '==', userId))
    const snap = await getDocs(membersQuery)
    for (const memberDoc of snap.docs) {
      const data = memberDoc.data() as OrgMember
      const orgId = memberDoc.ref.parent.parent?.id
      if (orgId) {
        await localDb.orgMembers.put({ ...data, id: memberDoc.id, orgId, userId })
      }
    }
    devLog.log(`Synced ${snap.size} memberships from Firestore for user ${userId}`)
  } catch (err) {
    console.error('syncOrgMembersFromFirestore failed:', err)
  }
}

/** Sincroniza los miembros de org del usuario a Firestore (necesario para reglas de permisos) */
export async function syncOrgMembersToFirestore(userId: string): Promise<void> {
  if (!isFirebaseConfigured() || !db) return
  try {
    const memberships = await localDb.orgMembers.where('userId').equals(userId).toArray()
    for (const member of memberships) {
      const memberRef = doc(db, 'organizations', member.orgId, 'members', userId)
      const snap = await getDoc(memberRef)
      if (!snap.exists()) {
        await setDoc(memberRef, member)
      }
    }
  } catch (err) {
    console.error('syncOrgMembersToFirestore failed:', err)
  }
}

// ============================================
// SPONSOR TIERS
// ============================================

export async function saveSponsorTier(tier: SponsorTier, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(tier)
  await localDb.sponsorTiers.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', tier.orgId, 'events', tier.eventId, 'sponsorTiers', tier.id), plain)
  }
}

export async function getEventSponsorTiers(eventId: string): Promise<SponsorTier[]> {
  return localDb.sponsorTiers.where('eventId').equals(eventId).sortBy('order')
}

export async function deleteSponsorTier(tier: SponsorTier): Promise<void> {
  await localDb.sponsorTiers.delete(tier.id)
  if (isFirebaseConfigured() && db) {
    const { deleteDoc } = await import('firebase/firestore')
    await deleteDoc(doc(db, 'organizations', tier.orgId, 'events', tier.eventId, 'sponsorTiers', tier.id))
  }
}

// ============================================
// EVENT SPONSORS
// ============================================

export async function saveEventSponsor(sponsor: EventSponsor, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(sponsor)
  await localDb.eventSponsors.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', sponsor.orgId, 'events', sponsor.eventId, 'sponsors', sponsor.id), plain)
  }
}

export async function getEventSponsors(eventId: string): Promise<EventSponsor[]> {
  return localDb.eventSponsors.where('eventId').equals(eventId).toArray()
}

export async function deleteEventSponsor(sponsor: EventSponsor): Promise<void> {
  await localDb.eventSponsors.delete(sponsor.id)
  await localDb.sponsorDeliverables.where('sponsorId').equals(sponsor.id).delete()
  if (isFirebaseConfigured() && db) {
    const { deleteDoc } = await import('firebase/firestore')
    await deleteDoc(doc(db, 'organizations', sponsor.orgId, 'events', sponsor.eventId, 'sponsors', sponsor.id))
  }
}

// ============================================
// SPONSOR DELIVERABLES
// ============================================

export async function saveSponsorDeliverable(d: SponsorDeliverable, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(d)
  await localDb.sponsorDeliverables.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', d.orgId, 'events', d.eventId, 'sponsorDeliverables', d.id), plain)
  }
}

export async function getSponsorDeliverables(eventId: string, sponsorId?: string): Promise<SponsorDeliverable[]> {
  let q = localDb.sponsorDeliverables.where('eventId').equals(eventId)
  if (sponsorId) {
    return localDb.sponsorDeliverables.where('sponsorId').equals(sponsorId).toArray()
  }
  return q.toArray()
}

export async function deleteSponsorDeliverable(d: SponsorDeliverable): Promise<void> {
  await localDb.sponsorDeliverables.delete(d.id)
  if (isFirebaseConfigured() && db) {
    const { deleteDoc } = await import('firebase/firestore')
    await deleteDoc(doc(db, 'organizations', d.orgId, 'events', d.eventId, 'sponsorDeliverables', d.id))
  }
}

// ============================================
// SPONSOR DOSSIERS (Phase 2)
// ============================================

export async function saveSponsorDossier(dossier: SponsorDossier, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(dossier)
  await localDb.sponsorDossiers.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', dossier.orgId, 'events', dossier.eventId, 'sponsorDossiers', dossier.id), plain)
  }
}

export async function getEventDossiers(eventId: string): Promise<SponsorDossier[]> {
  return localDb.sponsorDossiers.where('eventId').equals(eventId).toArray()
}

export async function getDossierByToken(token: string): Promise<SponsorDossier | null> {
  const local = await localDb.sponsorDossiers.where('token').equals(token).first()
  if (local) return local
  if (!isFirebaseConfigured() || !db) return null
  try {
    const snap = await getDocs(query(collectionGroup(db!, 'sponsorDossiers'), where('token', '==', token)))
    if (snap.empty) return null
    return { ...(snap.docs[0]!.data() as SponsorDossier), id: snap.docs[0]!.id }
  } catch { return null }
}

export async function deleteSponsorDossier(d: SponsorDossier): Promise<void> {
  await localDb.sponsorDossiers.delete(d.id)
  await localDb.dossierViews.where('dossierId').equals(d.id).delete()
  if (isFirebaseConfigured() && db) {
    const { deleteDoc } = await import('firebase/firestore')
    await deleteDoc(doc(db, 'organizations', d.orgId, 'events', d.eventId, 'sponsorDossiers', d.id))
  }
}

export async function saveDossierView(view: DossierView, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(view)
  await localDb.dossierViews.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', view.orgId, 'events', view.eventId, 'dossierViews', view.id), plain)
  }
}

export async function getDossierViews(dossierId: string): Promise<DossierView[]> {
  return localDb.dossierViews.where('dossierId').equals(dossierId).toArray()
}

/** Increment view count directly on Firestore (called from public view) */
export async function trackDossierView(dossier: SponsorDossier, referrer?: string): Promise<void> {
  if (!isFirebaseConfigured() || !db) return
  const { increment } = await import('firebase/firestore')
  const { updateDoc } = await import('firebase/firestore')
  try {
    const ref = doc(db, 'organizations', dossier.orgId, 'events', dossier.eventId, 'sponsorDossiers', dossier.id)
    await updateDoc(ref, { viewCount: increment(1), lastViewedAt: Date.now() })
    const view: DossierView = {
      id: crypto.randomUUID(),
      dossierId: dossier.id,
      eventId: dossier.eventId,
      orgId: dossier.orgId,
      viewedAt: Date.now(),
      referrer
    }
    await setDoc(doc(db, 'organizations', dossier.orgId, 'events', dossier.eventId, 'dossierViews', view.id), view)
  } catch (err) {
    console.error('trackDossierView failed:', err)
  }
}

// ============================================
// SPONSOR ACCESS TOKENS (Phase 3)
// ============================================

export async function saveSponsorAccessToken(token: SponsorAccessToken, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(token)
  await localDb.sponsorAccessTokens.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', token.orgId, 'events', token.eventId, 'sponsorAccessTokens', token.id), plain)
  }
}

export async function getSponsorAccessTokens(eventId: string, sponsorId?: string): Promise<SponsorAccessToken[]> {
  if (sponsorId) {
    return localDb.sponsorAccessTokens.where('sponsorId').equals(sponsorId).toArray()
  }
  return localDb.sponsorAccessTokens.where('eventId').equals(eventId).toArray()
}

export async function getSponsorByPortalToken(token: string): Promise<{ sponsor: EventSponsor; accessToken: SponsorAccessToken } | null> {
  const local = await localDb.sponsorAccessTokens.where('token').equals(token).first()
  if (local) {
    const sponsor = await localDb.eventSponsors.get(local.sponsorId)
    if (sponsor) return { sponsor, accessToken: local }
  }
  if (!isFirebaseConfigured() || !db) return null
  try {
    const snap = await getDocs(query(collectionGroup(db!, 'sponsorAccessTokens'), where('token', '==', token)))
    if (snap.empty) return null
    const accessToken = { ...(snap.docs[0]!.data() as SponsorAccessToken), id: snap.docs[0]!.id }
    const sponsorSnap = await getDoc(doc(db, 'organizations', accessToken.orgId, 'events', accessToken.eventId, 'sponsors', accessToken.sponsorId))
    if (!sponsorSnap.exists()) return null
    const sponsor = { ...(sponsorSnap.data() as EventSponsor), id: sponsorSnap.id }
    return { sponsor, accessToken }
  } catch { return null }
}

export async function deleteSponsorAccessToken(t: SponsorAccessToken): Promise<void> {
  await localDb.sponsorAccessTokens.delete(t.id)
  if (isFirebaseConfigured() && db) {
    const { deleteDoc } = await import('firebase/firestore')
    await deleteDoc(doc(db, 'organizations', t.orgId, 'events', t.eventId, 'sponsorAccessTokens', t.id))
  }
}

// ============================================
// SPONSOR MEDIA (Phase 3)
// ============================================

export async function saveSponsorMedia(media: SponsorMedia, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(media)
  await localDb.sponsorMedia.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', media.orgId, 'events', media.eventId, 'sponsorMedia', media.id), plain)
  }
}

export async function getSponsorMediaItems(eventId: string, sponsorId?: string): Promise<SponsorMedia[]> {
  if (sponsorId) {
    return localDb.sponsorMedia.where('sponsorId').equals(sponsorId).toArray()
  }
  return localDb.sponsorMedia.where('eventId').equals(eventId).toArray()
}

export async function deleteSponsorMedia(m: SponsorMedia): Promise<void> {
  await localDb.sponsorMedia.delete(m.id)
  if (isFirebaseConfigured() && db) {
    const { deleteDoc } = await import('firebase/firestore')
    await deleteDoc(doc(db, 'organizations', m.orgId, 'events', m.eventId, 'sponsorMedia', m.id))
  }
}

// ============================================
// ENROLLMENT
// ============================================

export async function saveEnrollmentConfig(config: EnrollmentConfig, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(config)
  await localDb.enrollmentConfigs.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'enrollmentConfigs', plain.id), plain)
    // Re-sync publicEvents with updated enrollment info
    const event = await localDb.events.where('id').equals(plain.eventId).first()
    if (event) await syncPublicEvent(event)
  }
}

export async function getEnrollmentConfig(eventId: string): Promise<EnrollmentConfig | undefined> {
  return localDb.enrollmentConfigs.where('eventId').equals(eventId).first()
}

export async function deleteEnrollmentConfig(config: EnrollmentConfig, opts?: SyncOptions): Promise<void> {
  const enrollments = await localDb.enrollments.where('configId').equals(config.id).toArray()
  for (const e of enrollments) { await deleteEnrollment(e, opts) }
  await localDb.enrollmentConfigs.delete(config.id)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    const { deleteDoc, doc: docRef } = await import('firebase/firestore')
    await deleteDoc(docRef(db, 'organizations', config.orgId, 'events', config.eventId, 'enrollmentConfigs', config.id))
  }
}

export async function getEnrollmentConfigByToken(token: string): Promise<EnrollmentConfig | null> {
  try {
    const local = await localDb.enrollmentConfigs.where('token').equals(token).first()
    if (local) return local
  } catch {
    // IndexedDB may not be available — fall through to Firestore
  }
  if (!isFirebaseConfigured() || !db) return null
  try {
    const snap = await getDocs(query(collectionGroup(db!, 'enrollmentConfigs'), where('token', '==', token), where('status', '==', 'open')))
    if (snap.empty) return null
    return { ...(snap.docs[0]!.data() as EnrollmentConfig), id: snap.docs[0]!.id }
  } catch (err) {
    console.error('Error fetching enrollment config by token:', err)
    return null
  }
}

export async function getEnrollmentConfigBySlug(slug: string): Promise<EnrollmentConfig | null> {
  try {
    const local = await localDb.enrollmentConfigs.where('slug').equals(slug).first()
    if (local) return local
  } catch {
    // IndexedDB may not be available — fall through to Firestore
  }
  if (!isFirebaseConfigured() || !db) return null
  try {
    const snap = await getDocs(query(collectionGroup(db!, 'enrollmentConfigs'), where('slug', '==', slug), where('status', '==', 'open')))
    if (snap.empty) return null
    return { ...(snap.docs[0]!.data() as EnrollmentConfig), id: snap.docs[0]!.id }
  } catch (err) {
    console.error('Error fetching enrollment config by slug:', err)
    return null
  }
}

export async function checkEnrollmentSlugAvailable(slug: string, currentConfigId?: string): Promise<boolean> {
  if (!isFirebaseConfigured() || !db) return true
  try {
    const snap = await getDocs(query(collectionGroup(db!, 'enrollmentConfigs'), where('slug', '==', slug)))
    if (snap.empty) return true
    return snap.docs.every(d => d.id === currentConfigId)
  } catch (err) {
    console.error('Error checking enrollment slug availability:', err)
    return false
  }
}

export async function getEnrollmentConfigBySlugOrToken(slugOrToken: string): Promise<EnrollmentConfig | null> {
  if (/^[0-9a-f]{16}$/.test(slugOrToken)) {
    return getEnrollmentConfigByToken(slugOrToken)
  }
  return getEnrollmentConfigBySlug(slugOrToken)
}

export async function getEventEnrollments(eventId: string): Promise<Enrollment[]> {
  return localDb.enrollments.where('eventId').equals(eventId).toArray()
}

export async function saveEnrollment(enrollment: Enrollment, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(enrollment)
  await localDb.enrollments.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'enrollments', plain.id), plain)
  }
}

export async function deleteEnrollment(enrollment: Enrollment, opts?: SyncOptions): Promise<void> {
  await localDb.enrollments.delete(enrollment.id)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    const { deleteDoc, doc: docRef } = await import('firebase/firestore')
    await deleteDoc(docRef(db, 'organizations', enrollment.orgId, 'events', enrollment.eventId, 'enrollments', enrollment.id))
  }
}

export async function submitPublicEnrollment(enrollment: Enrollment): Promise<void> {
  if (!isFirebaseConfigured() || !db) throw new Error('Firebase not configured')
  const plain = toPlainObject(enrollment)
  await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'enrollments', plain.id), plain)
  await waitForPendingWrites(db)
}

export async function getEnrollmentFromFirestore(enrollmentId: string, orgId: string, eventId: string): Promise<Enrollment | null> {
  if (!isFirebaseConfigured() || !db) return null
  try {
    const snap = await getDoc(doc(db, 'organizations', orgId, 'events', eventId, 'enrollments', enrollmentId))
    if (snap.exists()) return { ...(snap.data() as Enrollment), id: snap.id }
    return null
  } catch {
    return null
  }
}

export async function getEnrollmentConfigFromFirestore(configId: string, orgId: string, eventId: string): Promise<EnrollmentConfig | null> {
  if (!isFirebaseConfigured() || !db) return null
  try {
    const snap = await getDoc(doc(db, 'organizations', orgId, 'events', eventId, 'enrollmentConfigs', configId))
    if (snap.exists()) return { ...(snap.data() as EnrollmentConfig), id: snap.id }
    return null
  } catch {
    return null
  }
}

export async function getEnrollmentCountByConfig(configId: string): Promise<number> {
  if (!isFirebaseConfigured() || !db) {
    return localDb.enrollments.where('configId').equals(configId).count()
  }
  try {
    const config = await localDb.enrollmentConfigs.get(configId)
    if (!config) return 0
    const snap = await getDocs(collection(db, 'organizations', config.orgId, 'events', config.eventId, 'enrollments'))
    return snap.size
  } catch { return 0 }
}

export async function syncEnrollmentDataFromFirestore(orgId: string, eventId: string): Promise<void> {
  if (!isFirebaseConfigured() || !db) return
  try {
    const cfgSnap = await getDocs(collection(db, 'organizations', orgId, 'events', eventId, 'enrollmentConfigs'))
    for (const d of cfgSnap.docs) {
      await saveEnrollmentConfig({ ...(d.data() as EnrollmentConfig), id: d.id }, { skipSync: true })
    }
    const enrollSnap = await getDocs(collection(db, 'organizations', orgId, 'events', eventId, 'enrollments'))
    for (const d of enrollSnap.docs) {
      await saveEnrollment({ ...(d.data() as Enrollment), id: d.id }, { skipSync: true })
    }
  } catch (err) {
    console.error('syncEnrollmentDataFromFirestore failed:', err)
  }
}

export async function getUserEnrollmentsFromFirestore(userId: string, email?: string): Promise<(Enrollment & { eventName: string; eventSlug: string })[]> {
  if (!isFirebaseConfigured() || !db) return []
  try {
    // Primary: query by submittedBy (reliable, uid-based)
    const byUidSnap = await getDocs(query(collectionGroup(db, 'enrollments'), where('submittedBy', '==', userId)))
    const seen = new Set<string>()
    const results: (Enrollment & { eventName: string; eventSlug: string })[] = []

    for (const d of byUidSnap.docs) {
      const enrollment = { ...(d.data() as Enrollment), id: d.id }
      seen.add(enrollment.id)
      try {
        const eventSnap = await getDoc(doc(db!, 'organizations', enrollment.orgId, 'events', enrollment.eventId))
        if (eventSnap.exists()) {
          const ev = eventSnap.data() as Event
          results.push({ ...enrollment, eventName: ev.name, eventSlug: ev.slug })
        }
      } catch { /* event not readable */ }
    }

    // Fallback: query by contactEmail (for enrollments submitted before submittedBy was added)
    if (email) {
      try {
        const byEmailSnap = await getDocs(query(collectionGroup(db, 'enrollments'), where('contactEmail', '==', email)))
        for (const d of byEmailSnap.docs) {
          const enrollment = { ...(d.data() as Enrollment), id: d.id }
          if (seen.has(enrollment.id)) continue
          try {
            const eventSnap = await getDoc(doc(db!, 'organizations', enrollment.orgId, 'events', enrollment.eventId))
            if (eventSnap.exists()) {
              const ev = eventSnap.data() as Event
              results.push({ ...enrollment, eventName: ev.name, eventSlug: ev.slug })
            }
          } catch { /* event not readable */ }
        }
      } catch { /* contactEmail query may fail if index not ready */ }
    }

    return results
  } catch (err) {
    devLog.warn('getUserEnrollmentsFromFirestore failed:', err)
    return []
  }
}

// ============================================
// ENTITY DELEGATES
// ============================================

export async function saveEntityDelegate(delegate: EntityDelegate, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(delegate)
  await localDb.entityDelegates.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'entityDelegates', plain.id), plain)
  }
}

export async function getEntityDelegateByUser(eventId: string, userId: string): Promise<EntityDelegate | undefined> {
  return localDb.entityDelegates.where({ eventId, userId }).first()
}

export async function getEntityDelegatesForEvent(eventId: string): Promise<EntityDelegate[]> {
  return localDb.entityDelegates.where('eventId').equals(eventId).toArray()
}

export async function getEntityDelegateByUserFromFirestore(eventId: string, orgId: string, userId: string): Promise<EntityDelegate | undefined> {
  if (!isFirebaseConfigured() || !db) return undefined
  try {
    const { getDocs, collection, query, where } = await import('firebase/firestore')
    const snap = await getDocs(query(
      collection(db, 'organizations', orgId, 'events', eventId, 'entityDelegates'),
      where('userId', '==', userId)
    ))
    if (snap.empty) return undefined
    return { ...(snap.docs[0]!.data() as EntityDelegate), id: snap.docs[0]!.id }
  } catch { return undefined }
}

export async function deleteEntityDelegate(delegate: EntityDelegate, opts?: SyncOptions): Promise<void> {
  await localDb.entityDelegates.delete(delegate.id)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    const { deleteDoc, doc: docRef } = await import('firebase/firestore')
    await deleteDoc(docRef(db, 'organizations', delegate.orgId, 'events', delegate.eventId, 'entityDelegates', delegate.id))
  }
}

export async function getUserEntityDelegatesFromFirestore(userId: string): Promise<(EntityDelegate & { eventName: string; eventSlug: string })[]> {
  if (!isFirebaseConfigured() || !db) return []
  try {
    const snap = await getDocs(query(collectionGroup(db, 'entityDelegates'), where('userId', '==', userId)))
    const results: (EntityDelegate & { eventName: string; eventSlug: string })[] = []
    for (const d of snap.docs) {
      const delegate = { ...(d.data() as EntityDelegate), id: d.id }
      // Fetch event info from publicEvents (publicly readable)
      const eventSnap = await getDoc(doc(db!, 'organizations', delegate.orgId, 'events', delegate.eventId))
      if (eventSnap.exists()) {
        const ev = eventSnap.data() as Event
        results.push({ ...delegate, eventName: ev.name, eventSlug: ev.slug })
      }
    }
    return results
  } catch (err) {
    devLog.warn('getUserEntityDelegatesFromFirestore failed:', err)
    return []
  }
}

// ============================================
// EVENT NOTIFICATIONS
// ============================================

export async function saveEventNotification(notification: EventNotification, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(notification)
  await localDb.eventNotifications.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'notifications', plain.id), plain)
  }
}

export async function getEventNotifications(eventId: string): Promise<EventNotification[]> {
  const items = await localDb.eventNotifications.where('eventId').equals(eventId).toArray()
  return items.sort((a, b) => b.createdAt - a.createdAt)
}

export async function getEventNotificationsFromFirestore(orgId: string, eventId: string): Promise<EventNotification[]> {
  if (!isFirebaseConfigured() || !db) return []
  try {
    const { getDocs, collection } = await import('firebase/firestore')
    const snap = await getDocs(collection(db, 'organizations', orgId, 'events', eventId, 'notifications'))
    return snap.docs.map(d => ({ ...(d.data() as EventNotification), id: d.id }))
      .sort((a, b) => b.createdAt - a.createdAt)
  } catch { return [] }
}

export async function deleteEventNotification(n: EventNotification, opts?: SyncOptions): Promise<void> {
  await localDb.eventNotifications.delete(n.id)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    const { deleteDoc, doc: docRef } = await import('firebase/firestore')
    await deleteDoc(docRef(db, 'organizations', n.orgId, 'events', n.eventId, 'notifications', n.id))
  }
}

// ============================================
// NOTIFICATION RECEIPTS
// ============================================

export async function saveNotificationReceipt(receipt: NotificationReceipt, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(receipt)
  await localDb.notificationReceipts.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'organizations', plain.orgId, 'events', plain.eventId, 'notificationReceipts', plain.id), plain)
  }
}

export async function getUserNotificationReceipts(eventId: string, userId: string): Promise<NotificationReceipt[]> {
  return localDb.notificationReceipts.where({ eventId, userId }).toArray()
}

export async function getUserNotificationReceiptsFromFirestore(orgId: string, eventId: string, userId: string): Promise<NotificationReceipt[]> {
  if (!isFirebaseConfigured() || !db) return []
  try {
    const { getDocs, collection, query, where } = await import('firebase/firestore')
    const snap = await getDocs(query(
      collection(db, 'organizations', orgId, 'events', eventId, 'notificationReceipts'),
      where('userId', '==', userId)
    ))
    return snap.docs.map(d => ({ ...(d.data() as NotificationReceipt), id: d.id }))
  } catch { return [] }
}

export async function markReceiptRead(receipt: NotificationReceipt): Promise<void> {
  const updated = { ...receipt, read: true, readAt: Date.now() }
  await saveNotificationReceipt(updated)
}

// ============================================
// USER FCM TOKENS
// ============================================

export async function saveUserFcmToken(token: UserFcmToken, opts?: SyncOptions): Promise<void> {
  const plain = toPlainObject(token)
  await localDb.userFcmTokens.put(plain)
  if (!opts?.skipSync && isFirebaseConfigured() && db) {
    await setDoc(doc(db, 'userFcmTokens', plain.id), plain)
  }
}

export async function getUserFcmTokens(userId: string): Promise<UserFcmToken[]> {
  return localDb.userFcmTokens.where('userId').equals(userId).toArray()
}

// ============================================
// SUPERADMIN — reads all data from Firestore
// ============================================

export async function adminGetAllOrganizations(): Promise<Organization[]> {
  if (!isFirebaseConfigured() || !db) return localDb.organizations.toArray()
  const snap = await getDocs(collection(db, 'organizations'))
  return snap.docs.map(d => ({ ...(d.data() as Organization), id: d.id }))
}

export async function adminGetAllUsers(): Promise<User[]> {
  if (!isFirebaseConfigured() || !db) return localDb.users.toArray()
  const snap = await getDocs(collection(db, 'users'))
  return snap.docs.map(d => ({ ...(d.data() as User), id: d.id }))
}

export async function adminGetAllOrgMembers(): Promise<OrgMember[]> {
  if (!isFirebaseConfigured() || !db) return localDb.orgMembers.toArray()
  const results: OrgMember[] = []
  const orgsSnap = await getDocs(collection(db, 'organizations'))
  for (const orgDoc of orgsSnap.docs) {
    const membersSnap = await getDocs(collection(db, 'organizations', orgDoc.id, 'members'))
    for (const m of membersSnap.docs) {
      results.push({ ...(m.data() as OrgMember), id: m.id })
    }
  }
  return results
}

export async function adminGetAllEvents(): Promise<Event[]> {
  if (!isFirebaseConfigured() || !db) return localDb.events.toArray()
  const results: Event[] = []
  const orgsSnap = await getDocs(collection(db, 'organizations'))
  for (const orgDoc of orgsSnap.docs) {
    const evSnap = await getDocs(collection(db, 'organizations', orgDoc.id, 'events'))
    for (const ev of evSnap.docs) {
      results.push({ ...(ev.data() as Event), id: ev.id })
    }
  }
  return results
}

export async function adminUpdateOrgSubscription(orgId: string, subscription: Subscription): Promise<void> {
  const org = await getOrganization(orgId)
  if (!org) {
    if (!isFirebaseConfigured() || !db) throw new Error('Org not found')
    const snap = await getDoc(doc(db, 'organizations', orgId))
    if (!snap.exists()) throw new Error('Org not found')
    const firestoreOrg = { ...(snap.data() as Organization), id: snap.id }
    firestoreOrg.subscription = toPlainObject(subscription)
    await setDoc(doc(db, 'organizations', orgId), firestoreOrg)
    await saveOrganization(firestoreOrg, { skipSync: true })
    return
  }
  const updated: Organization = { ...org, subscription: toPlainObject(subscription) }
  await saveOrganization(updated)
}

/** Fetches all event-scoped data in a single org→event traversal to avoid repeating N+1 queries. */
export async function adminGetAllEventData(): Promise<{
  events: Event[]
  enrollments: Enrollment[]
  delegates: EntityDelegate[]
  entities: EventEntity[]
}> {
  const events: Event[] = []
  const enrollments: Enrollment[] = []
  const delegates: EntityDelegate[] = []
  const entities: EventEntity[] = []

  if (!isFirebaseConfigured() || !db) {
    return {
      events: await localDb.events.toArray(),
      enrollments: await localDb.enrollments.toArray(),
      delegates: await localDb.entityDelegates.toArray(),
      entities: await localDb.eventEntities.toArray()
    }
  }

  const orgsSnap = await getDocs(collection(db, 'organizations'))
  for (const orgDoc of orgsSnap.docs) {
    const orgId = orgDoc.id
    const evSnap = await getDocs(collection(db, 'organizations', orgId, 'events'))
    for (const evDoc of evSnap.docs) {
      events.push({ ...(evDoc.data() as Event), id: evDoc.id })
      const eventId = evDoc.id
      const base = `organizations/${orgId}/events/${eventId}`
      const [enrSnap, delSnap, entSnap] = await Promise.all([
        getDocs(collection(db, base, 'enrollments')),
        getDocs(collection(db, base, 'entityDelegates')),
        getDocs(collection(db, base, 'entities'))
      ])
      for (const d of enrSnap.docs) enrollments.push({ ...(d.data() as Enrollment), id: d.id })
      for (const d of delSnap.docs) delegates.push({ ...(d.data() as EntityDelegate), id: d.id })
      for (const d of entSnap.docs) entities.push({ ...(d.data() as EventEntity), id: d.id })
    }
  }
  return { events, enrollments, delegates, entities }
}
